/**
 * This file is auto-generated. Do not edit directly.
 * To update, run the generateIconImports.js script.
 */
import React, { useState, useEffect, useRef } from 'react';
import { IconProps } from './types';
import { mapIconName } from './utils/IconMapper';
import { DynamicIconName } from './types';
import { cn } from '@/lib/utils';

interface DynamicIconProps extends IconProps {
  iconName: DynamicIconName;
  fallback?: React.ReactNode;
  loadingFallback?: React.ReactNode;
  /**
   * Whether to use intersection observer to only load the icon when it's visible
   * @default true
   */
  lazyLoad?: boolean;
  /**
   * Root margin for the intersection observer
   * @default "100px"
   */
  rootMargin?: string;
  /**
   * Threshold for the intersection observer
   * @default 0
   */
  threshold?: number | number[];
  wrapperClassName?: string;
}

/**
 * DynamicIcon component that renders an icon based on its name
 * Uses dynamic imports to avoid including all icons in the bundle
 * Optimized for Vite's ES module system
 * Includes lazy loading with Intersection Observer
 * 
 * @param iconName - The name of the icon to render (with autocomplete support)
 * @param fallback - Optional fallback element to render if the icon is not found
 * @param loadingFallback - Optional fallback element to render while the icon is loading
 * @param lazyLoad - Whether to use intersection observer to only load the icon when it's visible
 * @param rootMargin - Root margin for the intersection observer
 * @param threshold - Threshold for the intersection observer
 * @param props - Other icon props like size, className, etc.
 */
export const DynamicIcon: React.FC<DynamicIconProps> = ({ 
  iconName, 
  fallback = null,
  loadingFallback = null,
  lazyLoad = true,
  rootMargin = "100px",
  threshold = 0,
  wrapperClassName,
  ...props 
}) => {
  const [IconComponent, setIconComponent] = useState<React.ComponentType<IconProps> | null>(null);
  const [isLoading, setIsLoading] = useState(lazyLoad);
  const [error, setError] = useState<Error | null>(null);
  const [isVisible, setIsVisible] = useState(!lazyLoad);
  const containerRef = useRef<HTMLDivElement>(null);

  // Set up intersection observer to detect when the icon is visible
  useEffect(() => {
    // If lazy loading is disabled, skip the intersection observer
    if (!lazyLoad) {
      setIsVisible(true);
      return;
    }

    const currentRef = containerRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // When the icon becomes visible, set isVisible to true
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          // Once visible, we don't need to observe anymore
          observer.disconnect();
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [lazyLoad, rootMargin, threshold]);

  // Load the icon when it becomes visible
  useEffect(() => {
    // Only load the icon if it's visible or if lazy loading is disabled
    if (!isVisible) return;

    // Map the icon name to the correct format
    const mappedIconName = mapIconName(iconName);
    
    // Reset state when icon name changes
    setIsLoading(true);
    setError(null);
    setIconComponent(null);

    // Dynamically import the icon component
    const importIcon = async () => {
      try {
        // For Vite, we use the more explicit path format
        // This ensures Vite can properly analyze and optimize the imports
        const module = await import(`../icons/icon-components/${mappedIconName}.tsx`);
        
        // In Vite, the component is typically the default export
        const Component = module.default || module[mappedIconName];
        
        if (Component) {
          setIconComponent(() => Component);
        } else {
          throw new Error(`Icon component not found in module: ${mappedIconName}`);
        }
      } catch (err) {
        console.warn(`Failed to load icon: ${mappedIconName}`, err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    importIcon();
  }, [iconName, isVisible]);

  // Render a container div that will be observed
  return (
    // <div ref={containerRef} style={{ display: 'inline-block' }} className={wrapperClassName}>
    <div ref={containerRef} className={cn('inline-block', wrapperClassName)}>
      {isLoading ? (
        // Show loading fallback while the icon is being loaded
        loadingFallback
      ) : error || !IconComponent ? (
        // Show error fallback if the icon failed to load
        fallback
      ) : (
        // Render the dynamically loaded icon component
        <IconComponent {...props} />
      )}
    </div>
  );
};
